import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import StripeKeysPanel from "@/components/StripeKeysPanel";
import LicensesPanel from "@/components/LicensesPanel";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import PixelsPanel from "@/components/PixelsPanel";
import api from "@/api";
import { Trash2, Radio, CreditCard, RefreshCw, Gift, XCircle, Loader2, AlertTriangle, KeyRound, BarChart3, Target, Users as UsersIcon, LayoutDashboard, Copy } from "lucide-react";
import { toast } from "sonner";

const GRANTABLE_TIERS = [
  { id: "studio", label: "Viral Book Studio — $97" },
  { id: "suite", label: "The Viral Book Suite — $497" },
  { id: "vip", label: "VIP — $997" },
  { id: "cohort", label: "Evergreen Cohort — $1,997" },
  { id: "dfy", label: "DFY Evergreen — $4,999" },
  { id: "pubglot_only", label: "PubGlot Only (license-only)" },
  { id: "cohort_plan", label: "Cohort 3-month plan" },
  { id: "dfy_plan", label: "DFY 3-month plan" },
];

const ADMIN_TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: UsersIcon },
  { id: "transactions", label: "Transactions", icon: CreditCard },
  { id: "licenses", label: "Licenses", icon: KeyRound },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "pixels", label: "Pixels", icon: Target },
  { id: "stripe", label: "Stripe", icon: Radio },
];

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState(null);
  const [tx, setTx] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(() => {
    try { return sessionStorage.getItem("vbs_admin_tab") || "overview"; }
    catch { return "overview"; }
  });

  useEffect(() => {
    try { sessionStorage.setItem("vbs_admin_tab", tab); } catch (_) { /* sessionStorage unavailable */ }
  }, [tab]);

  const load = async () => {
    try {
      const [u, s, m, t] = await Promise.all([
        api.get("/api/admin/users"),
        api.get("/api/admin/stats"),
        api.get("/api/admin/stripe-mode"),
        api.get("/api/admin/transactions"),
      ]);
      setUsers(u.data);
      setStats(s.data);
      setMode(m.data);
      setTx(t.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (uid) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/api/admin/users/${uid}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const [grantUserId, setGrantUserId] = useState(null);
  const [grantTier, setGrantTier] = useState("vip");
  const [grantBusy, setGrantBusy] = useState(false);

  const grant = async () => {
    if (!grantUserId || !grantTier) return;
    setGrantBusy(true);
    try {
      const { data } = await api.post(`/api/admin/users/${grantUserId}/grant`, { tier_id: grantTier });
      toast.success(`Granted ${grantTier} · now has: ${(data.entitlements || []).join(", ") || "—"}`);
      setGrantUserId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally {
      setGrantBusy(false);
    }
  };

  const revoke = async (uid, tierId) => {
    if (!window.confirm(`Revoke "${tierId}" from this user?`)) return;
    try {
      const { data } = await api.post(`/api/admin/users/${uid}/revoke`, { tier_id: tierId });
      toast.success(`Revoked ${tierId} · remaining: ${(data.entitlements || []).join(", ") || "none"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const [delTxId, setDelTxId] = useState(null);
  const [delTxBusy, setDelTxBusy] = useState(false);
  const [selectedTx, setSelectedTx] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleTx = (sid) => {
    setSelectedTx((prev) => {
      const n = new Set(prev);
      if (n.has(sid)) n.delete(sid); else n.add(sid);
      return n;
    });
  };
  const toggleAllTx = () => {
    if (selectedTx.size === tx.length && tx.length > 0) setSelectedTx(new Set());
    else setSelectedTx(new Set(tx.map((t) => t.session_id)));
  };
  const selectedCount = selectedTx.size;

  const deleteTransaction = async () => {
    if (!delTxId) return;
    setDelTxBusy(true);
    try {
      await api.delete(`/api/admin/transactions/${encodeURIComponent(delTxId)}`);
      toast.success("Transaction deleted from ledger");
      setDelTxId(null);
      setSelectedTx((prev) => {
        const n = new Set(prev); n.delete(delTxId); return n;
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDelTxBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (selectedCount === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/api/admin/transactions/bulk-delete", {
        session_ids: Array.from(selectedTx),
      });
      toast.success(`Deleted ${data.deleted_count} of ${data.requested} transactions`);
      setSelectedTx(new Set());
      setBulkConfirm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const setStripeMode = async (newMode) => {
    setSaving(true);
    try {
      await api.put("/api/admin/stripe-mode", { mode: newMode });
      toast.success(`Stripe switched to ${newMode.toUpperCase()} mode`);
      load();
    } catch (e) {
      toast.error("Failed to switch mode");
    } finally {
      setSaving(false);
    }
  };

  const modeIsLive = mode?.mode === "live";

  return (
    <Shell>
      <div className="vbs-page-eye">ADMIN</div>
      <h1 className="vbs-page-h">Suite <em>administration</em></h1>

      {/* Tab strip */}
      <div
        className="vbs-card"
        data-testid="admin-tabs"
        style={{
          padding: 6, marginBottom: 22, display: "flex", gap: 4,
          flexWrap: "wrap", overflowX: "auto",
        }}
      >
        {ADMIN_TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className="vbs-btn"
              style={{
                padding: "8px 14px",
                background: active ? "var(--gold)" : "transparent",
                color: active ? "#1a1300" : "var(--mid)",
                border: "none",
                borderRadius: 6,
                fontSize: 12,
                letterSpacing: 1,
                textTransform: "uppercase",
                fontWeight: active ? 700 : 500,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW tab */}
      {tab === "overview" && (
        <div data-testid="admin-tab-overview-content">
          {/* Stripe mode banner (read-only summary; full config is in Stripe tab) */}
          {mode && (
        <div className="vbs-card" style={{ padding: 22, marginBottom: 22, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }} data-testid="admin-stripe-card">
          <Radio size={22} style={{ color: modeIsLive ? "#e05040" : "#6dd4a0" }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, color: "var(--white)" }}>
              Stripe Mode
            </div>
            <div style={{ fontSize: 12.5, color: "var(--mid)", marginTop: 2 }}>
              Currently: <strong style={{ color: modeIsLive ? "#e05040" : "#6dd4a0" }} data-testid="stripe-current-mode">{(mode.mode || "test").toUpperCase()}</strong>
              {mode.publishable_key && (
                <span style={{ marginLeft: 10, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)" }}>
                  · {mode.publishable_key.slice(0, 12)}…
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "inline-flex", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
            <button
              onClick={() => setStripeMode("test")}
              disabled={saving || !mode.has_test_key}
              data-testid="stripe-mode-test"
              className="vbs-btn"
              style={{
                background: mode.mode === "test" ? "#6dd4a0" : "transparent",
                color: mode.mode === "test" ? "#041" : "var(--mid)",
                border: "none",
                borderRadius: 0,
                padding: "10px 20px",
              }}
            >
              Test / Demo
            </button>
            <button
              onClick={() => setStripeMode("live")}
              disabled={saving || !mode.has_live_key}
              data-testid="stripe-mode-live"
              className="vbs-btn"
              style={{
                background: modeIsLive ? "#e05040" : "transparent",
                color: modeIsLive ? "#fff" : "var(--mid)",
                border: "none",
                borderRadius: 0,
                padding: "10px 20px",
              }}
            >
              Live
            </button>
          </div>
        </div>
      )}

      {/* Stripe API Keys management — moved into the Stripe tab below */}

      {/* Suite stats */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 28 }}>
          <div className="vbs-stat">
            <div className="vbs-stat-lbl">Total users</div>
            <div className="vbs-stat-val" data-testid="stat-total-users">{stats.total_users}</div>
          </div>
          <div className="vbs-stat">
            <div className="vbs-stat-lbl">Translations</div>
            <div className="vbs-stat-val">{stats.total_translations}</div>
          </div>
          <div className="vbs-stat">
            <div className="vbs-stat-lbl">Completed</div>
            <div className="vbs-stat-val" style={{ color: "var(--teal)" }}>{stats.completed_translations}</div>
          </div>
          <div className="vbs-stat">
            <div className="vbs-stat-lbl">Transactions</div>
            <div className="vbs-stat-val">{tx.length}</div>
          </div>
        </div>
      )}
        </div>
      )}

      {/* USERS tab */}
      {tab === "users" && (
        <div data-testid="admin-tab-users-content">
      {/* Users */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, color: "var(--white)", margin: 0 }}>Users</h2>
        <button onClick={load} className="vbs-btn vbs-btn-ghost" style={{ padding: "6px 12px" }} data-testid="admin-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <div className="vbs-card" style={{ padding: 0, overflow: "hidden", marginBottom: 28 }}>
        <div style={{ overflowX: "auto" }}>
          <table className="vbs-table" data-testid="admin-users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Entitlements</th>
                <th>License used</th>
                <th>Key?</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td style={{ color: "var(--mid)" }}>{u.full_name || "—"}</td>
                  <td>
                    <span className={"vbs-chip " + (u.role === "admin" ? "processing" : "ok")}>{u.role}</span>
                  </td>
                  <td style={{ fontSize: 11, color: "var(--mid)" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 280 }}>
                      {(u.purchases || []).filter((p) => p !== "admin_grant").length === 0 ? (
                        <span style={{ color: "var(--muted)" }}>—</span>
                      ) : (
                        (u.purchases || []).filter((p) => p !== "admin_grant").map((p) => (
                          <span
                            key={p}
                            className="vbs-chip ok"
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", textTransform: "none" }}
                            title={`Click to revoke ${p}`}
                            onClick={() => revoke(u.id, p)}
                            data-testid={`revoke-chip-${u.id}-${p}`}
                          >
                            {p} <XCircle size={10} />
                          </span>
                        ))
                      )}
                    </div>
                    {u.entitlements?.length > 0 && (
                      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>
                        Access: {u.entitlements.join(", ")}
                      </div>
                    )}
                  </td>
                  <td>
                    {u.license_used ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span
                          data-testid={`user-license-${u.id}`}
                          style={{
                            fontFamily: "var(--font-mono, monospace)",
                            fontSize: 11,
                            color: "var(--gold)",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                          title={`Tier: ${u.license_used.tier_id} · click to copy code & jump to Licenses tab`}
                          onClick={() => {
                            navigator.clipboard?.writeText(u.license_used.code);
                            toast.success(`Copied ${u.license_used.code}`);
                            setTab("licenses");
                          }}
                        >
                          {u.license_used.code} <Copy size={9} />
                        </span>
                        <span style={{ fontSize: 10, color: "var(--muted)" }}>{u.license_used.tier_id}</span>
                      </div>
                    ) : (
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>—</span>
                    )}
                  </td>
                  <td>{u.anthropic_key_verified ? <span className="vbs-chip ok">yes</span> : <span className="vbs-chip err">no</span>}</td>
                  <td style={{ textAlign: "right" }}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button
                        className="vbs-btn vbs-btn-ghost"
                        onClick={() => { setGrantUserId(u.id); setGrantTier("vip"); }}
                        style={{ padding: "6px 10px", fontSize: 11 }}
                        data-testid={`grant-user-${u.id}`}
                        title="Grant offer package"
                      >
                        <Gift size={12} /> Grant
                      </button>
                      {u.role !== "admin" && (
                        <button className="vbs-btn vbs-btn-ghost" onClick={() => remove(u.id)} style={{ padding: "6px 10px" }} data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </div>
      )}

      {/* TRANSACTIONS tab */}
      {tab === "transactions" && (
        <div data-testid="admin-tab-transactions-content">
      {/* Transactions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, color: "var(--white)", margin: 0 }}>
          <CreditCard size={16} style={{ display: "inline", marginRight: 8, verticalAlign: "middle" }} />
          Recent Transactions
        </h2>
      </div>
      <div className="vbs-card" style={{ padding: 0, overflow: "hidden" }}>
        {/* Bulk action bar */}
        {selectedCount > 0 && (
          <div
            style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              padding: "10px 14px",
              background: "rgba(224,80,64,0.06)",
              borderBottom: "1px solid rgba(224,80,64,0.28)",
            }}
            data-testid="bulk-tx-bar"
          >
            <AlertTriangle size={14} style={{ color: "#ff8f80" }} />
            <span style={{ fontSize: 13, color: "var(--bright)" }}>
              <strong style={{ color: "#ff8f80" }}>{selectedCount}</strong> past transaction{selectedCount === 1 ? "" : "s"} selected
            </span>
            <span style={{ flex: 1 }} />
            <button
              onClick={() => setSelectedTx(new Set())}
              className="vbs-btn vbs-btn-ghost"
              style={{ padding: "6px 12px", fontSize: 11 }}
              data-testid="bulk-tx-clear"
            >
              Clear selection
            </button>
            <button
              onClick={() => setBulkConfirm(true)}
              className="vbs-btn vbs-btn-coral"
              style={{ padding: "6px 14px", fontSize: 12 }}
              data-testid="bulk-tx-delete"
            >
              <Trash2 size={12} /> Delete selected
            </button>
          </div>
        )}
        <div style={{ overflowX: "auto" }}>
          <table className="vbs-table" data-testid="admin-tx-table">
            <thead>
              <tr>
                <th style={{ width: 38, paddingRight: 0 }}>
                  <input
                    type="checkbox"
                    checked={tx.length > 0 && selectedCount === tx.length}
                    onChange={toggleAllTx}
                    aria-label="Select all transactions"
                    data-testid="bulk-tx-select-all"
                  />
                </th>
                <th>When</th>
                <th>User</th>
                <th>Tier</th>
                <th>Amount</th>
                <th>Mode</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {tx.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 28, color: "var(--muted)" }}>
                    No transactions yet.
                  </td>
                </tr>
              )}
              {tx.map((t) => (
                <tr
                  key={t.session_id}
                  style={selectedTx.has(t.session_id) ? { background: "rgba(201,168,76,0.06)" } : undefined}
                >
                  <td style={{ width: 38, paddingRight: 0 }}>
                    <input
                      type="checkbox"
                      checked={selectedTx.has(t.session_id)}
                      onChange={() => toggleTx(t.session_id)}
                      aria-label={`Select transaction ${t.session_id}`}
                      data-testid={`bulk-tx-check-${t.session_id}`}
                    />
                  </td>
                  <td style={{ fontSize: 12, color: "var(--mid)" }}>{new Date(t.created_at).toLocaleString()}</td>
                  <td>{t.user_email}</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{t.tier_id}</td>
                  <td>${t.amount}</td>
                  <td>
                    <span className={"vbs-chip " + (t.stripe_mode === "live" ? "err" : "ok")} style={{ textTransform: "uppercase" }}>
                      {t.stripe_mode}
                    </span>
                  </td>
                  <td>
                    <span className={"vbs-chip " + (t.payment_status === "paid" ? "ok" : t.status === "expired" ? "err" : "processing")}>
                      {t.payment_status}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="vbs-btn vbs-btn-ghost"
                      onClick={() => setDelTxId(t.session_id)}
                      style={{ padding: "5px 9px", fontSize: 11 }}
                      data-testid={`delete-tx-${t.session_id}`}
                      title="Delete transaction from ledger"
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </div>
      )}

      {/* LICENSES tab */}
      {tab === "licenses" && (
        <div data-testid="admin-tab-licenses-content">
          <LicensesPanel />
        </div>
      )}

      {/* ANALYTICS tab */}
      {tab === "analytics" && (
        <div data-testid="admin-tab-analytics-content">
          <AnalyticsPanel />
        </div>
      )}

      {/* PIXELS tab */}
      {tab === "pixels" && (
        <div data-testid="admin-tab-pixels-content">
          <PixelsPanel />
        </div>
      )}

      {/* STRIPE tab */}
      {tab === "stripe" && (
        <div data-testid="admin-tab-stripe-content">
          <StripeKeysPanel />
        </div>
      )}

      {/* Grant-tier modal */}
      {grantUserId && (
        <div
          onClick={() => !grantBusy && setGrantUserId(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.72)",
            display: "grid", placeItems: "center", zIndex: 100, padding: 20,
          }}
          data-testid="grant-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="vbs-card"
            style={{ padding: 28, maxWidth: 460, width: "100%" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <Gift size={18} style={{ color: "var(--gold)" }} />
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "var(--bright)" }}>
                Grant <em style={{ color: "var(--gold)" }}>offer package</em>
              </div>
            </div>
            <p style={{ fontSize: 13, color: "var(--mid)", marginBottom: 18 }}>
              Assign any tier to <strong style={{ color: "var(--bright)" }}>{users.find((u) => u.id === grantUserId)?.email || "this user"}</strong>. The user will see the tier's tools unlock immediately. No charge is made.
            </p>
            <label style={{ fontSize: 11, letterSpacing: 2, color: "var(--gold-dim)", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
              Tier / Package
            </label>
            <select
              className="vbs-input"
              value={grantTier}
              onChange={(e) => setGrantTier(e.target.value)}
              disabled={grantBusy}
              data-testid="grant-tier-select"
              style={{ width: "100%", marginBottom: 20 }}
            >
              {GRANTABLE_TIERS.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setGrantUserId(null)}
                className="vbs-btn vbs-btn-ghost"
                disabled={grantBusy}
                data-testid="grant-cancel"
              >
                Cancel
              </button>
              <button
                onClick={grant}
                className="vbs-btn vbs-btn-gold"
                disabled={grantBusy}
                data-testid="grant-confirm"
              >
                {grantBusy ? <><Loader2 size={12} className="animate-spin" /> Granting…</> : <><Gift size={12} /> Grant Access</>}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete-transaction confirm modal */}
      {delTxId && (
        <div
          onClick={() => !delTxBusy && setDelTxId(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.72)",
            display: "grid", placeItems: "center", zIndex: 100, padding: 20,
          }}
          data-testid="delete-tx-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="vbs-card"
            style={{ padding: 28, maxWidth: 460, width: "100%" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <AlertTriangle size={18} style={{ color: "#ff8f80" }} />
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "var(--bright)" }}>
                Delete <em style={{ color: "var(--gold)" }}>transaction</em>?
              </div>
            </div>
            <p style={{ fontSize: 13, color: "var(--mid)", marginBottom: 8, lineHeight: 1.65 }}>
              This removes the row from the admin ledger. It does <strong style={{ color: "#ff8f80" }}>not</strong> refund Stripe and does <strong style={{ color: "#ff8f80" }}>not</strong> revoke the user's tier access.
            </p>
            <div
              style={{
                fontFamily: "var(--font-mono, monospace)", fontSize: 11,
                color: "var(--gold-dim)", marginBottom: 18,
                padding: "8px 10px", background: "rgba(201,168,76,0.06)",
                border: "1px solid var(--border)", borderRadius: 4,
                wordBreak: "break-all",
              }}
            >
              {delTxId}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                onClick={() => setDelTxId(null)}
                className="vbs-btn vbs-btn-ghost"
                disabled={delTxBusy}
                data-testid="delete-tx-cancel"
              >
                Cancel
              </button>
              <button
                onClick={deleteTransaction}
                className="vbs-btn vbs-btn-coral"
                disabled={delTxBusy}
                data-testid="delete-tx-confirm"
              >
                {delTxBusy ? <><Loader2 size={12} className="animate-spin" /> Deleting…</> : <><Trash2 size={12} /> Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk-delete transactions confirm modal */}
      {bulkConfirm && (
        <div
          onClick={() => !bulkBusy && setBulkConfirm(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.72)",
            display: "grid", placeItems: "center", zIndex: 100, padding: 20,
          }}
          data-testid="bulk-confirm-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="vbs-card"
            style={{ padding: 28, maxWidth: 460, width: "100%" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <AlertTriangle size={18} style={{ color: "#ff8f80" }} />
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "var(--bright)" }}>
                Delete <em style={{ color: "var(--gold)" }}>{selectedCount}</em> transaction{selectedCount === 1 ? "" : "s"}?
              </div>
            </div>
            <p style={{ fontSize: 13, color: "var(--mid)", marginBottom: 18, lineHeight: 1.65 }}>
              These rows will be removed from the admin ledger in one pass. This does <strong style={{ color: "#ff8f80" }}>not</strong> refund Stripe and does <strong style={{ color: "#ff8f80" }}>not</strong> revoke any user's tier access.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                onClick={() => setBulkConfirm(false)}
                className="vbs-btn vbs-btn-ghost"
                disabled={bulkBusy}
                data-testid="bulk-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={bulkDelete}
                className="vbs-btn vbs-btn-coral"
                disabled={bulkBusy}
                data-testid="bulk-confirm-delete"
              >
                {bulkBusy ? <><Loader2 size={12} className="animate-spin" /> Deleting…</> : <><Trash2 size={12} /> Delete {selectedCount}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
